'use strict';

const $ = require('dibs-jquery-shim');
const SV = require('server-vars');
const Clump = require('bunsen/libraries/clump');
const Permission = require('bunsen/models/identity/Permission');
const { sessionStorage } = require('dibs-browser-storage');
const { fetchQuery, graphql } = require('react-relay');

const { getEnvironment } = require('../../relayEnvironmnentProvider.es');
const User = require('../../models/User');

const contains = require('lodash.contains');
const get = require('lodash.get');

const CUSTOMER_TYPE_VIP = 'VIP';
const CUSTOMER_TYPE_TRADE = 'Trade';
const CUSTOMER_TYPE_CONSUMER = 'Consumer';

const CONVERSATION_STATUS_CONTACTED = 'dealer contacted';
const CONVERSATION_STATUS_NOT_CONTACTED = 'no dealer contacted';

const STORAGE_TRANSACTIONS_STATUS = 'userTransactionsStatus';

const BUYER_STATUS_CONFIRMED_MULTIPLE = 'CONFIRMED_MULTIPLE';
const BUYER_STATUS_CONFIRMED_ONE = 'CONFIRMED_ONE';
const BUYER_STATUS_SUBMITTED = 'SUBMITTED';
const BUYER_STATUS_INITIATED = 'INITIATED';
const BUYER_STATUS_NONE = 'NONE';

const BUYER_STATUSES_MAP = {
    [BUYER_STATUS_CONFIRMED_MULTIPLE]: 'confirmed|multiple orders',
    [BUYER_STATUS_CONFIRMED_ONE]: 'confirmed|one order',
    [BUYER_STATUS_SUBMITTED]: 'submitted|none confirmed',
    [BUYER_STATUS_INITIATED]: 'initiated|none submitted',
    [BUYER_STATUS_NONE]: 'no orders initiated',
};

const TRADE_STATUS_MAP = {
    VERIFIED_VETTED: 'verified - vetted',
    VERIFIED_PRE_APPROVED: 'verified - pre-approved',
    SUSPECTED: 'suspected',
    APPLIED_IN_REVIEW: 'applied - in review',
    APPLIED_NOT_ENOUGH_INFORMATION: 'applied - not enough information',
    APPLIED_REJECTED: 'applied - rejected',
};

const FIRST_PURCHASE_RANGES = {
    10000: '> $10K',
    5000: '$5K to $10K',
    3000: '$3K to $5K',
    1000: '$1K to $3K',
    0: '< $1K',
};

let detailsPromise;
let transactionsStatusPromise;

require('bunsen/libraries/plugins/jquery.cookie');

/**
 * @param {string|number} dateString
 * @returns {string}
 */
function formatDate(dateString) {
    const date = new Date(dateString);

    return `${date.getMonth() + 1}-${date.getFullYear()}`;
}

/**
 * @returns {string} - first ga session date for user
 */
function getFirstUtmSessionDate() {
    const utmaCookie = $.cookie('__utma');
    const timestamp = utmaCookie.split('.')[2] * 1000;

    return formatDate(timestamp);
}

function getTradeStatus() {
    const roles = User.getCurrentUser().get('roles');
    let status;
    let date;
    let verifiedDate;

    if (User.loggedIn() && roles && roles.length) {
        const role = roles.toJSON().find(r => r.name === User.BUYER_TRADE);
        const statusKey = get(role, 'userData.userStatus.name');
        status = TRADE_STATUS_MAP[statusKey];
        date = get(role, 'createdDate');
        verifiedDate = get(role, 'userData.verifiedDate');
    }

    return { status, date, verifiedDate };
}

const tradeFirmQuery = graphql`
    query userDetails_esQuery($userId: String!) {
        viewer {
            user(userId: $userId) {
                tradeFirm {
                    serviceId
                    tier
                }
            }
        }
    }
`;

function fetchTradeFirm() {
    const userId = User.getUserId();

    if (!userId) {
        return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
        fetchQuery(getEnvironment(), tradeFirmQuery, {
            userId,
        })
            .then(data => {
                const tradeFirm = get(data, 'viewer.user.tradeFirm');
                resolve(tradeFirm);
            })
            .catch(error => {
                console.warn('Trade firm query failed. Error:', error);
                reject(error);
            });
    });
}

/**
 * @param {Number} userId - signed in user's id
 * @returns {object} - the base object that needs to be pushed into the dataLayer when a user registers for the site
 */
function getRegisterBaseDetails(userId) {
    return {
        loginStatus: 'Logged In',
        userID: userId,
        registrationStatus: 'Registered',
        userEmailAddress: User.getEmail(),
        emailToken: User.getEmailToken(),
        defaultCurrency: 'USD Default',
        tradeStatus: getTradeStatus().status,
        customerType: CUSTOMER_TYPE_CONSUMER,
    };
}

/**
 * @param {Number} userId - signed in user's id
 * @param {string} userCurrency - signed in user's currency
 * @returns {object} - the base object that needs to be pushed into the dataLayer when a user signs into the site
 */
function getLoginBaseDetails(userId, userCurrency, userEmail) {
    return {
        loginStatus: 'Logged In',
        userID: userId,
        registrationStatus: 'Registered',
        userEmailAddress: userEmail,
        defaultCurrency: `${userCurrency} Selected`,
        buyerStatus: undefined,
        buyerActivityStatus: undefined,
        tradeStatus: getTradeStatus().status,
        firstPurchasePrice: 'na',
        firstPurchaseDate: 'na',
        contactDealerStatus: undefined,
        customerType: undefined,
        firstSessionDate: getFirstUtmSessionDate(),
    };
}

/**
 * @param {array} permissionsList
 * @returns {string} customerType - Consumer, VIP, Trade or VIP and Trade
 */
function getCustomerType(permissionsList) {
    const permissions = permissionsList.pluck('name');
    const customerType = [];

    if (contains(permissions, Permission.PERMISSION_B_TRADE_CHECKOUT)) {
        customerType.push(CUSTOMER_TYPE_TRADE);
    }

    if (contains(permissions, Permission.PERMISSION_B_VIP_CHECKOUT)) {
        customerType.push(CUSTOMER_TYPE_VIP);
    }

    if (!customerType.length) {
        return CUSTOMER_TYPE_CONSUMER;
    }

    return customerType.join(' and ');
}

/**
 * @param {object} clump - clump instance to handle a transactions fetch
 * @param {string} userId
 * @param {string} userToken
 */
function setupTransactions(clump, userId, userToken) {
    clump.add({
        url: `/soa/graphql/?${User.COOKIE_USER}=${userToken}`,
        type: 'POST',
        data: {
            query: `{
                viewer {
                    conversations(userId: "${userId}", first: 0) {
                        totalResults
                    }
                    allTransactions: transactions(buyerId: ["${userId}"], first: 0) {
                        totalResults
                    }
                    initiatedTransactions: transactions(buyerId: ["${
                        userId
                    }"], viewName: "buyerTracking.initiated", first: 0) {
                        totalResults
                    }
                    submittedTransactions: transactions(buyerId: ["${
                        userId
                    }"], viewName: "buyerTracking.submitted", first: 0) {
                        totalResults
                    }
                    confirmedTransactions: transactions(buyerId: ["${
                        userId
                    }"], sort: "createdDate asc", viewName: "buyerTracking.confirmed", first: 1) {
                        totalResults
                        edges {
                            node {
                                mostRecentOffer {
                                    createdDate
                                    subTotalAmount {
                                        convertedAmounts {
                                            USD
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }`,
        },
    });
}

/**
 * @param {object} userConfig
 * @param {object} userConfig.user
 * @param {string} userConfig.userId
 * @param {string} userConfig.userEmail
 * @param {string} [userConfig.userCurrency]
 */
function getRegisteredDetails(userConfig) {
    const details = getRegisterBaseDetails(userConfig.userId);
    return Promise.resolve(details);
}

/**
 * @param {object} conversations
 * @returns {string}
 */
function getContactDealerStatus(viewer) {
    if (viewer && viewer.conversations && viewer.conversations.totalResults) {
        return CONVERSATION_STATUS_CONTACTED;
    }
    return CONVERSATION_STATUS_NOT_CONTACTED;
}

/**
 * @param {Object} transactions
 * @returns {Object}
 */
function getBuyerStatusFromTransactions(transactions) {
    const { initiatedTransactions, submittedTransactions, confirmedTransactions } = transactions;
    let buyerStatus;

    if (confirmedTransactions && confirmedTransactions.totalResults > 1) {
        buyerStatus = BUYER_STATUS_CONFIRMED_MULTIPLE;
    } else if (confirmedTransactions && confirmedTransactions.totalResults) {
        buyerStatus = BUYER_STATUS_CONFIRMED_ONE;
    } else if (submittedTransactions && submittedTransactions.totalResults) {
        buyerStatus = BUYER_STATUS_SUBMITTED;
    } else if (initiatedTransactions && initiatedTransactions.totalResults) {
        buyerStatus = BUYER_STATUS_INITIATED;
    } else {
        buyerStatus = BUYER_STATUS_NONE;
    }

    return { buyerStatus };
}

/**
 * @param {Object} transactions
 * @returns {Object}
 */
function getFirstPurchaseInfo(transactions) {
    const { confirmedTransactions } = transactions;
    let firstPurchasePrice;
    let firstPurchaseDate;

    if (confirmedTransactions && confirmedTransactions.totalResults) {
        const price = get(
            confirmedTransactions,
            'edges[0].node.mostRecentOffer.subTotalAmount.convertedAmounts.USD'
        );
        const date = get(confirmedTransactions, 'edges[0].node.mostRecentOffer.createdDate');

        // Format date
        if (date) {
            firstPurchaseDate = formatDate(date);
        }

        // Format price range
        if (price) {
            for (const range in FIRST_PURCHASE_RANGES) {
                if (price >= range) {
                    firstPurchasePrice = FIRST_PURCHASE_RANGES[range];
                }
            }
        }
    }

    return {
        firstPurchasePrice,
        firstPurchaseDate,
    };
}

/**
 * @param {Object} payload
 *      {Object} payload.transactions - transactions response with counts
 *      {Boolean} payload.gaLabel - return gaLabel instead of constant
 * @returns {Object}
 */
function getTransactionsStatus(payload = { gaLabel: true }) {
    const { viewer, gaLabel } = payload;
    let totalTransactions = 0;
    let transactionsStatus = {
        buyerStatus: BUYER_STATUS_NONE,
        totalTransactions,
    };

    if (viewer) {
        totalTransactions = viewer.allTransactions.totalResults;
        transactionsStatus = getBuyerStatusFromTransactions(viewer);
        Object.assign(transactionsStatus, getFirstPurchaseInfo(viewer), { totalTransactions });
        transactionsStatus.contactDealerStatus = getContactDealerStatus(viewer);
        sessionStorage.setItem(STORAGE_TRANSACTIONS_STATUS, transactionsStatus);
    } else {
        transactionsStatus =
            sessionStorage.getItem(STORAGE_TRANSACTIONS_STATUS) || transactionsStatus;
    }

    if (gaLabel && transactionsStatus) {
        transactionsStatus.buyerStatusKey = transactionsStatus.buyerStatus;
        transactionsStatus.buyerStatus = BUYER_STATUSES_MAP[transactionsStatus.buyerStatus];
    }

    return transactionsStatus;
}

/**
 * Some pages need fresh buyer status, so this is the place to re-validate it
 */
function checkTransactionsStatusCache() {
    if (
        SV.get('settings.pageType') === 'checkout' &&
        SV.get('settings.pageSubType') === 'confirmation'
    ) {
        sessionStorage.deleteItem(STORAGE_TRANSACTIONS_STATUS);
    }
}

/**
 * @param {Boolean} gaLabel
 * @returns {Promise}
 */
function fetchTransactionsStatus(gaLabel = true) {
    const clump = new Clump();
    const cachedTransactionsStatus = sessionStorage.getItem(STORAGE_TRANSACTIONS_STATUS);

    checkTransactionsStatusCache();

    if (User.loggedIn() && !cachedTransactionsStatus) {
        setupTransactions(clump, User.getUserId(), User.getUserToken());

        if (!transactionsStatusPromise) {
            transactionsStatusPromise = clump.run().then(response => {
                const viewer = get(
                    response,
                    'result.clumpResponse.elements[0].payload.data.viewer'
                );
                return getTransactionsStatus({ viewer, gaLabel });
            });
        }
        return transactionsStatusPromise;
    }

    return Promise.resolve(getTransactionsStatus({ gaLabel }));
}

/**
 * @param {object} userConfig
 * @param {object} userConfig.user
 * @param {string} userConfig.userId
 * @param {string} userConfig.userToken
 * @param {string} userConfig.userEmail
 * @param {string} userConfig.userCurrency
 * @param {object} currentClump, mainly used for testing
 */
function getLoggedInDetails(userConfig, currentClump) {
    const { user, userId, userToken, userCurrency } = userConfig;
    const clump = currentClump || new Clump();

    if (detailsPromise) {
        return detailsPromise;
    }

    setupTransactions(clump, userId, userToken);

    detailsPromise = Promise.all([clump.run(), User.load()]).then(response => {
        const viewer = get(response[0], 'result.clumpResponse.elements[0].payload.data.viewer');
        const details = getLoginBaseDetails(userId, userCurrency, User.getEmail());

        const contactDealerStatus = getContactDealerStatus(viewer);
        const customerType = getCustomerType(user.get('permissions.permissionList'));
        const {
            buyerStatus,
            firstPurchasePrice = 'na',
            firstPurchaseDate = 'na',
        } = getTransactionsStatus({
            gaLabel: true,
            viewer,
        });

        Object.assign(details, {
            customerType,
            buyerStatus,
            firstPurchasePrice,
            firstPurchaseDate,
            contactDealerStatus,
        });

        return details;
    });

    return detailsPromise;
}

module.exports = {
    getTransactionsStatus,
    fetchTransactionsStatus,
    getLoggedInDetails,
    getRegisteredDetails,
    getTradeStatus,
    fetchTradeFirm,
    BUYER_STATUSES_MAP,
};



// WEBPACK FOOTER //
// ./assets/js/global/tracking/helpers/userDetails.es.js